import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { SkudoBaseChartComponent } from '../skudo-base-chart/skudo-base-chart.component';
import {
  getDefaultAxisRenderer,
  getDefaultCursor,
  getDefaultSerieOptionsAtIndex,
  getLayoutByType,
} from './skudo-line-series-chart.helper';
import {
  getDefaultChartTooltipOptions,
  parseToColor,
  getTooltip,
  getGradient,
} from '../../helpers/charts.helpers';
import {
  LineSerieChartInput,
  LineSerieOptions,
  LineSeriesCursorOptions,
  CHART_LAYOUT_TYPE,
  AXIS_TYPE,
  LineSeriesRenderOptions,
  ChartAxisTooltip,
} from 'emma-common-ts/skudo';
import { SelectOption } from '@platform/types';
import { find } from 'lodash';

@Component({
    selector: 'skudo-line-series-chart',
    templateUrl: './skudo-line-series-chart.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SkudoLineSeriesChartComponent
  extends SkudoBaseChartComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  // Inherited fields
  @Input() override identifier = `skudo-line-chart-div`;
  @Input() override loading = false;

  // Chart Options
  @Input() layoutType = CHART_LAYOUT_TYPE.VERTICAL;
  @Input() cursorOptions: LineSeriesCursorOptions = getDefaultCursor();
  @Input() axisRendererOptions: LineSeriesRenderOptions[] = getDefaultAxisRenderer();
  @Input() tooltipOptions: ChartAxisTooltip[] = [
    { axisType: AXIS_TYPE.X, tooltip: getDefaultChartTooltipOptions() },
  ];

  // Embbeded portlet options
  @Input() title = '';
  @Input() description = '';
  @Input() error = '';
  selectionTitle = '';
  @Input() bigTitle = false;

  // Select options
  @Input() selectOptions: SelectOption[] = [];
  @Input() selectCurrentValue?: string;
  @Input() selectLabelKey = 'label';
  @Input() selectValueKey = 'value';
  @Input() selectMaxlength = 0;
  @Input() selectSearchable = true;
  @Input() selectSortable = false;
  @Output() $selectedChange = new EventEmitter<string>();

  protected chart!: am5xy.XYChart;
  protected yAxis!: am5xy.Axis<am5xy.AxisRenderer>;
  protected xAxis!: am5xy.Axis<am5xy.AxisRenderer>;
  protected legend!: am5.Legend;
  protected categoryField = 'label';
  protected valueField = 'value';

  protected bullets!: am5.Bullet;

  // eslint-disable-next-line @typescript-eslint/ban-types
  constructor(@Inject(PLATFORM_ID) protected override platformId: Object, protected override zone: NgZone) {
    super(platformId, zone);
  }

  ngAfterViewInit(): void {
    this.browserOnly(() => {
      this.initChart();
      this.chart = this.root.container.children.push(
        am5xy.XYChart.new(this.root, {
          panY: false,
          layout: getLayoutByType(this.layoutType, this.root),
        })
      );
      // Create Legend
      this.legend = this.chart.children.unshift(
        am5.Legend.new(this.root, {
          nameField: 'name',
          fillField: 'color',
          centerX: am5.percent(50),
          x: am5.percent(50),
          layout: am5.GridLayout.new(this.root, {
            maxColumns: 4,
            fixedWidthGrid: true,
          }),
          clickTarget: 'itemContainer',
          marginTop: 20,
          paddingBottom: 20,
        })
      );
      // Create Y-Axis
      this.yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          min: 0,
          renderer: am5xy.AxisRendererY.new(this.root, {}),
        })
      );
      // Create X-Axis
      this.xAxis = this.chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          renderer: am5xy.AxisRendererX.new(this.root, {}),
          categoryField: this.categoryField,
        })
      );
      this.setTitle();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('selectCurrentValue' in changes) {
      this.setTitle();
    }
  }

  private setTitle() {
    if (this.selectOptions.length) {
      const option = find(this.selectOptions, { value: this.selectCurrentValue });
      if (option) {
        this.selectionTitle = option.label;
      }
    }
  }

  updateSerie(input: LineSerieChartInput, options?: LineSerieOptions): void {
    this.browserOnly(() => {
      this.whenChartIsInitialized(() => {
        this.removeSerie(input.index);
        this.addSerie(input, options);
      });
    });
  }

  private addCursor(): void {
    this.chart.set(
      'cursor',
      am5xy.XYCursor.new(this.root, {
        snapToSeriesBy: this.cursorOptions.snapToAxis,
        xAxis: this.xAxis,
        yAxis: this.yAxis,
      })
    );
    const cursor = this.chart.get('cursor');
    this.cursorOptions?.lines?.forEach((cursorOption) => {
      if (cursorOption.axisType) {
        const cursorLine = cursorOption.axisType === AXIS_TYPE.X ? cursor?.lineX : cursor?.lineY;
        cursorLine?.setAll({
          visible: cursorOption.grid?.visible,
          stroke: parseToColor(cursorOption.grid?.strokeColor),
          strokeWidth: cursorOption.grid?.strokeWidth,
          strokeDasharray: cursorOption.grid?.strokeDasharray,
        });
      }
    });
  }

  removeSerie(index: number): void {
    if (this.chart?.series.hasIndex(index)) {
      this.chart.series.removeIndex(index).dispose();
      const cursor = this.chart.get('cursor');
      cursor?.dispose();
      if (this.legend.data.hasIndex(index)) {
        this.legend.data.removeIndex(index);
      }
    }
  }

  reset(): void {
    if (this.chart) {
      this.chart.series.each((serie, index) => {
        this.removeSerie(index);
      });
      if (this.legend.data.length) {
        this.legend.data.clear();
      }
    }
  }

  addSerie(input: LineSerieChartInput, options?: LineSerieOptions): void {
    this.browserOnly(() => {
      const serieOptions = {
        ...getDefaultSerieOptionsAtIndex(input.index),
        ...options,
      };
      const serieColor = parseToColor(serieOptions?.color);

      this.xAxis.data.setAll(
        input.labels.map((label) => {
          return {
            label,
          };
        })
      );

      const serie = this.chart.series.insertIndex(
        input.index,
        am5xy.LineSeries.new(this.root, {
          name: input.name,
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          valueYField: this.valueField,
          categoryXField: this.categoryField,
          stroke: serieColor,
          tooltip: getTooltip(this.root, serieOptions?.tooltip),
          legendLabelText: input?.legendLabelText,
        })
      );

      serie.data.setAll(input.data);

      serie.set('fill', serieColor);

      serie.appear(500, 500);

      serie.strokes.template.setAll({
        strokeWidth: serieOptions?.strokes?.strokeWidth,
      });

      serie.fills.template.setAll({
        fillGradient: getGradient(this.root, serieOptions?.fills?.linearGradient),
        fillOpacity: serieOptions?.fills?.opacity,
        visible: serieOptions?.fills?.visible,
      });

      if (serieOptions?.showLegend) {
        const series = this.chart.series.values;
        this.legend.data.setAll(
          series.map((serie) => {
            return {
              name: serie._settings.legendLabelText,
              color: serie._settings.fill,
            };
          })
        );
      }

      serie.bullets.push(function (root) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 2.4,
            fill: serie.get('stroke'),
          }),
        });
      });

      this.addTooltipToAxis();
      this.addRenderForLabels();
      this.addCursor();
    });
  }

  private addRenderForLabels = (): void => {
    const yRenderer = this.yAxis.get('renderer');
    const xRenderer = this.xAxis.get('renderer');

    this.axisRendererOptions.forEach((rendererOptions) => {
      const rendererAxis = rendererOptions.axisType === AXIS_TYPE.X ? xRenderer : yRenderer;
      rendererAxis.labels.template.setAll({
        fill: parseToColor(rendererOptions.labels?.color),
        fontSize: rendererOptions.labels?.fontSize,
        fontWeight: rendererOptions.labels?.fontWeight,
        paddingTop: rendererOptions.labels?.paddingTop,
      });
      rendererAxis.grid.template.setAll({
        visible: rendererOptions.grid?.visible,
        stroke: parseToColor(rendererOptions.grid?.strokeColor),
        strokeWidth: rendererOptions.grid?.strokeWidth,
      });
    });
  };

  private addTooltipToAxis(): void {
    this.tooltipOptions?.forEach((tooltipOption) => {
      const currentAxis = tooltipOption.axisType === AXIS_TYPE.X ? this.xAxis : this.yAxis;
      currentAxis.set('tooltip', getTooltip(this.root, tooltipOption?.tooltip));
    });
  }

  onSelectChangeValue(selected: string): void {
    this.reset();
    this.setTitle();
    this.$selectedChange.emit(selected);
  }
}
